// Package imports:
import React, { useEffect, useRef, useState } from "react";
// Component imports:
import Button from "../../../ui-elements/Button/Button";
import Modal from "../../../ui-elements/Modal/Modal";
import ProgressBar from "../../../ui-elements/ProgressBar/ProgressBar";
// Type imports:
import { KnowYourCustomer } from "../../../types/DueDiligenceTypes";
import Table from "../../../ui-elements/Table/Table";
import { Fetched } from "../../../types/Types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/pro-solid-svg-icons";
import Spinner from "react-bootstrap/esm/Spinner";
import Alert from "../../../ui-elements/Alert/Alert";
import { insertDash } from "../../../services/utils";

interface IProps {
    show: boolean;
    close: () => void;
    // Optional prop - if provided, will update the parent's customer state when KYC reports are fetched
    setCustomers?: React.Dispatch<React.SetStateAction<Fetched<KnowYourCustomer[]>>>;
    selectedCustomers: KnowYourCustomer[];
    companyKYCPrice: number;
    clearChecked: () => void;
}

interface TableRow {
    customerId: number;
    companyId: number;
    companyName: string;
    companySSN: string;
    message: string;
    status: string;
    loading: boolean;
}

interface BuyKycReportError {
    CompanyId: number;
    ErrorMsg: string;
}

// Number of companies to poll simultaneously in each batch
const POLL_BATCH_SIZE = 20;
// Time to wait between polling cycles (2 seconds)
const POLL_INTERVAL = 2 * 1000;

// Status constants to track the state of each KYC report request
const STATUS_IN_PROGRESS = 'Sæki KYC skýrslu';
const STATUS_SAVED = 'KYC skýrsla sótt';
const STATUS_STARTED = 'Könnun hafin';
const STATUS_ERROR = 'Villa';

const GetKYCOverviewCompaniesModal: React.FC<IProps> = ({
    show,
    close,
    setCustomers,
    selectedCustomers,
    companyKYCPrice,
    clearChecked
}) => {
    // Initialize table rows with all selected customers set to "in progress" state
    const [rows, setRows] = useState<TableRow[]>(() => selectedCustomers.map(c => ({
        customerId: c.customerId,
        companyId: c.companyId,
        companyName: c.companyName,
        companySSN: c.companySSN,
        message: 'Sæki KYC skýrslu',
        status: STATUS_IN_PROGRESS,
        loading: true
    })));

    const [MODAL_IS_CLOSEABLE, setMODAL_IS_CLOSABLE] = useState(true);
    // Controls whether we're actively polling for status updates
    const [polling, setPolling] = useState(false);
    // Reference to the polling timeout so we can clean it up
    const pollingRef = useRef<NodeJS.Timeout | null>(null);
    // Keep a ref to rows to avoid stale closure issues in the polling loop
    const rowsRef = useRef<TableRow[]>([]);
    // Track whether the form has been submitted
    const [hasSubmitted, setHasSubmitted] = useState(false);
    // Count of completed operations (success or error)
    const [completedCount, setCompletedCount] = useState(0);
    // Track whether all operations are complete
    const [completed, setCompleted] = useState(false);
    // Store any general errors (like permission errors)
    const [error, setError] = React.useState<string | null>(null);

    // Keep rowsRef synchronized with rows state for use in polling callback
    useEffect(() => {
        rowsRef.current = rows;
    }, [rows]);

    /**
     * Helper function to update a specific row in the table by companyId
     * @param companyId - The ID of the company to update
     * @param updates - Partial row object with fields to update
     */
    const updateRow = (companyId: number, updates: Partial<TableRow>) => {
        setRows(prev => prev.map(r => r.companyId === companyId ? { ...r, ...updates } : r));
    };

    /**
     * Helper function to update a customer in the parent component's state
     * Only runs if setCustomers prop is provided
     * @param customerId - The ID of the customer to update
     * @param updates - Partial customer object with fields to update
     */
    const updateCustomer = (customerId: number, updates: Partial<KnowYourCustomer>) => {
        if (!setCustomers) return;

        setCustomers(prev => {
            // Don't update if we have an error state or no customers
            if (!prev || prev instanceof Error) return prev;

            // Map through customers and update the matching one
            return prev.map(c =>
                c.customerId === customerId
                    ? { ...c, ...updates }
                    : c
            );
        });
    };

    // Reset all state when modal opens or closes
    useEffect(() => {
        if (show) {
            // When opening, reset to initial state with all selected customers
            setRows(selectedCustomers.map(c => ({
                customerId: c.customerId,
                companyId: c.companyId,
                companyName: c.companyName,
                companySSN: c.companySSN,
                message: 'Sæki KYC skýrslu',
                status: STATUS_IN_PROGRESS,
                loading: true
            })));
        } else {
            // When closing, clear all state
            setRows([]);
            setHasSubmitted(false);
            setPolling(false);
            setCompletedCount(0);
            setError(null);
            setMODAL_IS_CLOSABLE(true);
            if (pollingRef.current) {
                clearTimeout(pollingRef.current);
                pollingRef.current = null;
            }
            setCompleted(false);
        }

        // If closing after submission, clear all checkboxes in parent
        if (!show && hasSubmitted) {
            clearChecked();
        }
    }, [show]);

    /**
     * Main submission handler - initiates the KYC report fetching process
     * Sends a request to start fetching KYC reports for all selected customers
     * Then begins polling to track progress
     */
    const handleSubmit = async () => {
        setHasSubmitted(true);
        setMODAL_IS_CLOSABLE(false);

        const customerIds = selectedCustomers.map(c => c.customerId);

        fetch('/DueDiligence/BuyKycReports', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(customerIds)
        })
            .then(async resp => {
                // Status 202 means partial success - some companies had errors
                if (resp.status === 202) {
                    const errors: BuyKycReportError[] = await resp.json();
                    errors.forEach(err => {
                        updateRow(err.CompanyId, {
                            message: err.ErrorMsg,
                            status: STATUS_ERROR,
                            loading: false
                        });
                    });
                }
                setMODAL_IS_CLOSABLE(true);
                // Start polling for status updates on all companies
                setPolling(true);
            })
            .catch(() => {
                // If the entire request fails, mark all companies as error
                setRows(prev => prev.map(r => ({
                    ...r,
                    message: 'Villa við að hefja ferli',
                    status: STATUS_ERROR,
                    loading: false
                })));
            });
    };

    // Helper functions to check row status
    const isDone = (status: string) => status === STATUS_SAVED || status === STATUS_STARTED;
    const isInProgress = (status: string) => status === STATUS_IN_PROGRESS;
    const isError = (status: string) => status === STATUS_ERROR;

    /**
     * Polling effect - continuously checks status of in-progress KYC reports
     * Runs in batches to avoid overwhelming the server
     * Stops when all reports are complete or an error occurs
     */
    useEffect(() => {
        if (!polling) return;
        let stopped = false;

        const poll = async () => {
            // Only poll companies that are still in progress
            const inProgress = rowsRef.current.filter(r => isInProgress(r.status));
            if (inProgress.length === 0) {
                setPolling(false);
                return;
            }

            // Process companies in batches to manage server load
            for (let i = 0; i < inProgress.length; i += POLL_BATCH_SIZE) {
                const batch = inProgress.slice(i, i + POLL_BATCH_SIZE);

                // Poll all companies in this batch simultaneously
                await Promise.all(batch.map(async row => {
                    try {
                        const resp = await fetch(`/DueDiligence/GetBuyKycStatus/${row.customerId}`);

                        // Handle permission errors - stop polling and show error
                        if (resp.status === 403) {
                            const error = await resp.text();
                            setPolling(false);
                            setError(error);
                            setRows(prev => prev.map(r => isInProgress(r.status) ? {
                                ...r,
                                message: error ?? 'Þú hefur ekki aðgang að þessu fyrirtæki',
                                status: STATUS_ERROR,
                                loading: false
                            } : r));
                            return;
                        }

                        if (!resp.ok) throw new Error('Villa við að sækja stöðu');

                        // Parse the response and update based on status
                        const data = await resp.json();

                        if (isDone(data.status)) {
                            setMODAL_IS_CLOSABLE(true);
                            // Update the row to show completion
                            updateRow(row.companyId, {
                                status: data.status,
                                message: data.statusDescription || 'Könnun hafin',
                                loading: false
                            });

                            // Update the customer in parent component state if setCustomers is provided
                            // This ensures the parent's customer list reflects the new status
                            if (setCustomers) {
                                updateCustomer(row.customerId, {
                                    status: data.status,
                                    riskscoreCreated: new Date().toISOString(),
                                    riskscoreId: data.riskscoreId
                                });
                            }

                            setCompletedCount(prev => prev + 1);
                        } else if (isInProgress(data.status)) {
                            // Still in progress, update message if provided
                            updateRow(row.companyId, {
                                status: STATUS_IN_PROGRESS,
                                message: data.statusDescription || 'Sæki KYC skýrslu',
                                loading: true
                            });
                        } else if (isError(data.status)) {
                            // Error occurred, mark as complete but with error status
                            updateRow(row.companyId, {
                                status: STATUS_ERROR,
                                message: data.statusDescription || 'Villa',
                                loading: false
                            });

                            // Update customer status in parent if setCustomers provided
                            if (setCustomers) {
                                updateCustomer(row.customerId, {
                                    status: STATUS_ERROR
                                });
                            }

                            setCompletedCount(prev => prev + 1);
                        } else {
                            // Unknown status - treat as error
                            updateRow(row.companyId, {
                                status: STATUS_ERROR,
                                message: data.statusDescription || 'Óþekkt staða',
                                loading: false
                            });

                            if (setCustomers) {
                                updateCustomer(row.customerId, {
                                    status: STATUS_ERROR
                                });
                            }

                            setCompletedCount(prev => prev + 1);
                        }
                    } catch (e) {
                        // Network error or other issue - keep trying
                        updateRow(row.companyId, {
                            message: 'Ekki tókst að sækja gögn, reyni aftur.',
                            loading: true
                        });
                    }
                }));
            }

            // Schedule next poll if there are still in-progress items
            if (!stopped && rowsRef.current.some(r => isInProgress(r.status))) {
                pollingRef.current = setTimeout(poll, POLL_INTERVAL);
            } else {
                setPolling(false);
            }
        };

        // Start first poll after initial interval
        pollingRef.current = setTimeout(poll, POLL_INTERVAL);

        // Cleanup function to stop polling if component unmounts
        return () => {
            stopped = true;
            if (pollingRef.current) clearTimeout(pollingRef.current);
        };
    }, [polling, setCustomers]);

    // Debounce the completed state to prevent rapid updates
    useEffect(() => {
        const handler = setTimeout(() => {
            setCompleted(completedCount === selectedCustomers.length);
        }, 500);
        return () => clearTimeout(handler);
    }, [completedCount, show]);

    /**
     * Helper function to render the status icon/text for each row
     * Shows spinner for loading, check mark for success, or error text
     */
    const getRowStatus = (row: TableRow) => {
        if (row.loading) return <Spinner className='loading-spinner' animation="border" />;
        if (isDone(row.status)) return <FontAwesomeIcon icon={faCheck} />;
        if (isError(row.status)) return 'Villa';
        return '';
    }

    /**
     * Handles modal close action
     * Stops active polling and clears the polling timeout before closing
     * Only allows closing if MODAL_IS_CLOSEABLE is true
     */
    const handleClose = () => {
        if (MODAL_IS_CLOSEABLE) {
            // Stop polling by setting the state to false
            setPolling(false);

            // Clear the timeout to prevent any pending poll from executing
            if (pollingRef.current) {
                clearTimeout(pollingRef.current);
                pollingRef.current = null;
            }

            // Close the modal
            close();
        }
    }
    return (
        <Modal
            show={show}
            onHide={handleClose}
            dontCloseOnClickOutside
            header={"Hefja áreiðanleikakönnun"}
            hasCloseButton={MODAL_IS_CLOSEABLE}
            body={
                // Show different content based on whether form has been submitted
                !hasSubmitted
                    ? <div className="get-kyc-overview">
                        {/* Initial view - confirmation before starting */}
                        <div className="info-area">
                            <p className="paragraph--bold">Þú ert að fara að framkvæma áreiðanleikakönnun á {selectedCustomers.length} lögaðila.</p>
                            <Table
                                scrollable
                                maxBodyHeight="270px"
                                tableSize="sm"
                                data={selectedCustomers}
                                columns={[{
                                    title: "Lögaðili",
                                    renderCell: ({ companyName }) => companyName,
                                    textAlign: "left"
                                }, {
                                    title: "Kennitala",
                                    renderCell: ({ companySSN }) => insertDash(companySSN),
                                    textAlign: "left"
                                }, {
                                    title: "Könnun síðast hafin",
                                    renderCell: ({ reportTimestamp }) => {
                                        return reportTimestamp ? new Date(reportTimestamp).toLocaleDateString('is-IS') : '';
                                    },
                                    textAlign: "left"
                                }]}
                            />
                            <p className="paragraph"><span className="paragraph--bold">Ath. hver framkvæmd áreiðanleikakönnun kostar {companyKYCPrice.toLocaleString('de-DE')} kr. án vsk.</span> Innifalið er m.a. sótt gildandi skráning lögaðila frá Hlutafélagaskrá m/ raunverulegum eigendum, helstu uppflettingar á prókúruhöfum og eigendum á móti válistum (m.a. íslenskur PEP listi, alþjóðlegir válistar og áhættusöm ríkisföng), rafrænar innskráningar, spurningalisti sendur út og móttekinn, skráð áhættumat og gagnageymsla.</p>
                            <p className="paragraph--bold">Viltu halda áfram?</p>
                        </div>

                        <div style={{ marginTop: 24 }}>
                            <Button buttonType="secondary" onClick={handleClose}>Til baka</Button>
                            <Button style={{ marginLeft: 20 }} onClick={handleSubmit}>Staðfesta</Button>
                        </div>

                    </div>
                    : !error ? <div className="get-kyc-overview">
                        {!MODAL_IS_CLOSEABLE && <p className="paragraph--bold">Vinsamlegast ekki loka þessum glugga á meðan vinnsla er í gangi.</p>}
                        {/* Progress view - show status while processing */}
                        {!completed &&
                            <ProgressBar
                                current={completedCount}
                                total={selectedCustomers.length}
                                showPercentage={true}
                                size="large"
                            />
                        }
                        <p className="paragraph">Áreiðanleikakönnun er hafin hjá {completedCount} lögaðilum.</p>
                        {rows.some(row => row.status === STATUS_ERROR) &&
                            <p className="paragraph">Ekki tókst að hefja áreiðanleikakönnun hjá {rows.filter(row => row.status === STATUS_ERROR).length} lögaðilum.</p>
                        }
                        <Table
                            scrollable
                            maxBodyHeight="270px"
                            tableSize="sm"
                            data={rows}
                            columns={[{
                                title: "Nafn",
                                renderCell: ({ companyName }) => companyName,
                                textAlign: "left"
                            }, {
                                title: "Kennitala",
                                renderCell: ({ companySSN }) => insertDash(companySSN),
                                textAlign: "left"
                            }, {
                                title: "Skilaboð",
                                renderCell: ({ message }) => message === STATUS_IN_PROGRESS ? "Sæki gögn" : message,
                                textAlign: "left"
                            }, {
                                title: "Staða",
                                renderCell: (row) => getRowStatus(row),
                                textAlign: "center"
                            }]}
                        />
                        {MODAL_IS_CLOSEABLE && <Button style={{ marginTop: '20px' }} onClick={handleClose}>Loka</Button>}
                    </div>
                        : <div>
                            {/* Error view - show error message */}
                            <Alert type="error" headText={error} />
                        </div>
            }
        />
    );
};

export default GetKYCOverviewCompaniesModal;